import React, { useEffect, useState } from 'react';
import {
  Wrapper,
  Button,
  useBreadcrumbRoutes,
  FormTitle,
  SubContent,
  Form,
  ShowInput,
  Tag,
  Tooltip,
} from '@maxtropy/components';
import { Space, Row, Col } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { AlarmLevelDisplay } from '@/pages/PvAlarm/types';
import { AlarmLevel, AlarmLevelColorDisplay } from '@/shared/types';
import {
  apiV2DeviationRateAlarmRecordDetailPost,
  V2DeviationRateAlarmRecordDetailPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import dayjs from 'dayjs';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useDeviationRecordOptions } from './service';
import { isNil } from 'lodash-es';

const DeviationRecordDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [info, setInfo] = useState<V2DeviationRateAlarmRecordDetailPostResponse>();
  const [loading, setLoading] = useState(false);
  // const { optionsMap } = useDeviationRecordOptions();

  useEffect(() => {
    if (id) {
      setLoading(true);
      apiV2DeviationRateAlarmRecordDetailPost({ id })
        .then(res => {
          setInfo(res);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [id]);

  const routes = [{ name: '报警详情' }];

  const handleBack = () => {
    navigate('/deviation/alarm/record');
  };

  // 格式化统计时间范围
  const formatStatTime = () => {
    if (info?.statCycleFromTime && info?.statCycleToTime) {
      return `${dayjs(info.statCycleFromTime).format('YYYY-MM-DD HH:mm:ss')} ~ ${dayjs(info.statCycleToTime).format(
        'YYYY-MM-DD HH:mm:ss'
      )}`;
    }
    return '--';
  };

  // 解析设备名称/ids字符串为数组
  const parseStringToArray = (deviceNames?: string, gap: string = ',') => {
    if (!deviceNames) return [];
    return deviceNames.split(gap).filter(Boolean);
  };

  return (
    <Wrapper routes={[...(breadcrumbRoutes?.routes ?? []), ...routes]} isLoading={loading}>
      <FormTitle title="报警详情"></FormTitle>

      <SubContent style={{ marginBottom: 0 }}>
        <Form layout="vertical">
          {/* 第一行：报警信息、报警等级、能源介质拓扑 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="报警信息">
                <ShowInput value={info?.name || '--'}></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="报警等级">
                {info?.alarmLevel !== undefined && info?.alarmLevel !== null ? (
                  <Tag border="solid" color={AlarmLevelColorDisplay[info.alarmLevel as AlarmLevel]}>
                    {AlarmLevelDisplay[info.alarmLevel as AlarmLevel]}
                  </Tag>
                ) : (
                  '--'
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="能源介质拓扑">
                <ShowInput value={info?.energyMediumTopologyName || '--'}></ShowInput>
              </Form.Item>
            </Col>
          </Row>

          {/* 第二行：拓扑节点、能源介质、统计周期 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="拓扑节点">
                <ShowInput value={info?.energyMediumTopologyNodeName || '--'}></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="能源介质">
                <ShowInput value={info?.energyMediumName || '--'}></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="统计周期">
                <ShowInput value={info?.statCycleTypeDesc || '--'}></ShowInput>
              </Form.Item>
            </Col>
          </Row>

          {/* 第三行：偏差率、偏差量、统计时间 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label={
                  <span>
                    偏差率&nbsp;
                    <Tooltip
                      overlayInnerStyle={{ width: 320 }}
                      title="根据设定的统计周期, 查询统计周期内所选择能源介质拓扑对应的拓扑节点对应的能源介质的偏差率绝对值, 若大于等于输入的值, 则报警；"
                    >
                      <ExclamationCircleOutlined style={{ color: 'var(--warning-color)' }} />
                    </Tooltip>
                  </span>
                }
              >
                <ShowInput
                  value={!isNil(info?.deviationRate) ? `${info?.deviationRate.toFixed(2)}%` : '--'}
                ></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="偏差量">
                <ShowInput
                  value={
                    !isNil(info?.deviationAmount)
                      ? `${info?.deviationAmount.toFixed(2)}${info?.physicalUnitName || ''}`
                      : '--'
                  }
                ></ShowInput>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="统计时间">
                <ShowInput value={formatStatTime()}></ShowInput>
              </Form.Item>
            </Col>
          </Row>

          {/* 第四行：报警时间、恢复时间 */}
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="报警时间">
                <ShowInput
                  value={info?.alarmTime ? dayjs(info.alarmTime).format('YYYY-MM-DD HH:mm:ss') : '--'}
                ></ShowInput>
              </Form.Item>
            </Col>
            {/*<Col span={8}>*/}
            {/*  <Form.Item label="恢复时间">*/}
            {/*    <ShowInput*/}
            {/*      value={info?.recoverTime ? dayjs(info.recoverTime).format('YYYY-MM-DD HH:mm:ss') : '--'}*/}
            {/*    ></ShowInput>*/}
            {/*  </Form.Item>*/}
            {/*</Col>*/}
          </Row>

          {/* 第五行：输入设备 */}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item label="拓扑节点-对应的输入设备">
                {info?.topologyNodeEntryDeviceNames ? (
                  <>
                    {parseStringToArray(info?.topologyNodeEntryDeviceNames).map((device, index, arr) => (
                      <React.Fragment key={index}>
                        <a
                          style={{ color: 'var(--mx-primary-color)' }}
                          onClick={() =>
                            window.open(
                              `/data/history/device?deviceId=${
                                parseStringToArray(info?.topologyNodeEntryDeviceIds)[index]
                              }`
                            )
                          }
                        >
                          {device}
                        </a>
                        {index < arr.length - 1 && <span style={{ color: 'var(--mx-primary-color)' }}>、</span>}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  '--'
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* 第六行：输出设备 */}
          <Row>
            <Col span={24}>
              <Form.Item label="拓扑节点-对应的输出设备">
                {info?.topologyNodeExitDeviceNames ? (
                  <>
                    {parseStringToArray(info?.topologyNodeExitDeviceNames).map((device, index, arr) => (
                      <React.Fragment key={index}>
                        <a
                          style={{ color: 'var(--mx-primary-color)' }}
                          onClick={() =>
                            window.open(
                              `/data/history/device?deviceId=${
                                parseStringToArray(info?.topologyNodeExitDeviceIds)[index]
                              }`
                            )
                          }
                        >
                          {device}
                        </a>
                        {index < arr.length - 1 && <span style={{ color: 'var(--mx-primary-color)' }}>、</span>}
                      </React.Fragment>
                    ))}
                  </>
                ) : (
                  '--'
                )}
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </SubContent>

      <Space size={8} className="sticky-footer">
        <Button onClick={handleBack}>返回</Button>
      </Space>
    </Wrapper>
  );
};

export default DeviationRecordDetail;
